import React from 'react';
import { Heart } from 'lucide-react';
import { useCart } from '../../../contexts/CartContext';
import { CART_CONSTANTS } from '../FuncationsCartAndWishlist/Dto/AddToCardtUtilityDto';
import './styles/SeachCards.css';

interface ProductCardProps {
  id: string;
  title: string;
  price: string;
  image: string;
  slug: string;
  deliveryTag?: boolean;
  onAddToCart?: () => void;
  onMoreInfo?: () => void;
  onAddToWishlist?: () => void;
  isWishlisted?: boolean;
  quantity?: number;
  product?: any;
  onViewOptions?: (product: any) => void;
};

const SearchCards: React.FC<ProductCardProps> = ({
  id,
  title,
  price,
  image,
  deliveryTag = true,
  onAddToCart,
  onMoreInfo,
  onAddToWishlist,
  isWishlisted = false,
  quantity,
  product,
  onViewOptions
}) => {
  const { cartItems, updateQuantity, removeFromCart } = useCart();

  const cartItem = cartItems.find(item => {
    if (item.productId !== id) return false;
    return !item.variantSku || item.variantSku === '';
  });

  const isInCart = !!cartItem;
  const cartQuantity = cartItem?.quantity || 0;

  const handleWishlistClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (onAddToWishlist) {
      onAddToWishlist();
    }
  };

  const handleCartQuantityChange = (newQuantity: number) => {
    if (!cartItem) return;

    if (newQuantity <= 0) {
      removeFromCart(cartItem.productId, cartItem.variantSku, cartItem.variantId);
    } else {
      updateQuantity(cartItem.productId, newQuantity, cartItem.variantSku, cartItem.variantId);
    }
  };

  const handleDecreaseClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    handleCartQuantityChange(cartQuantity - 1);
  };

  const handleIncreaseClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (cartQuantity < CART_CONSTANTS.MAX_QUANTITY) {
      handleCartQuantityChange(cartQuantity + 1);
    }
  };

  const handleAddToCartClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (onAddToCart) {
      onAddToCart();
    }
  };

  const handleCardClick = () => {
    if (onMoreInfo) {
      onMoreInfo();
    }
  };

  const outOfStock = typeof quantity === 'number' ? quantity === 0 : false;

  return (
    <div className="search-card" onClick={handleCardClick} style={{ cursor: 'pointer', position: 'relative' }}>
      {/* Out of Stock Badge */}
      {outOfStock && (
        <div style={{position: 'absolute', top: 8, left: 8, background: '#ccc', color: '#fff', padding: '2px 8px', borderRadius: '8px', fontSize: '0.8rem', zIndex: 20}}>
          Out of Stock
        </div>
      )}
      <div
        className="wishlist-icon"
        title={isWishlisted ? 'Remove from Wishlist' : 'Add to Wishlist'}
        onClick={handleWishlistClick}
        onTouchEnd={(e) => { e.stopPropagation(); if (onAddToWishlist) { onAddToWishlist(); } }}
        role="button"
        aria-label={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
      >
        <Heart color={isWishlisted ? '#e0245e' : '#000000ff'} strokeWidth={2.2} fill={isWishlisted ? '#e0245e' : 'none'} />
      </div>

      {deliveryTag && (
        <div className="delivery-tag">
          30 min Delivery
        </div>
      )}

      <div className="search-card-image-container">
        <img 
          src={image} 
          alt={title}
          className="search-card-image"
        />
      </div>

      <div className="search-card-info">
        <h3 className="search-card-title">{title}</h3>
        <div className="search-card-price-actions">
          {!product?.variants || product.variants.length === 0 ? (
            <span className="search-card-price">{price}</span>
          ) : null}
          {isInCart ? (
            <div className="cart-quantity-controls">
              <button
                type="button"
                className="cart-quantity-btn"
                onClick={handleDecreaseClick}
                style={{ cursor: 'pointer', zIndex: 10, position: 'relative' }}
                aria-label="Decrease quantity"
              >
                -
              </button>
              <span className="cart-quantity-display">{cartQuantity}</span>
              <button
                type="button"
                className="cart-quantity-btn"
                onClick={handleIncreaseClick}
                style={{ cursor: 'pointer', zIndex: 10, position: 'relative' }}
                aria-label="Increase quantity"
                disabled={cartQuantity >= CART_CONSTANTS.MAX_QUANTITY}
              >
                +
              </button>
            </div>
          ) : product?.variants && product.variants.length > 0 ? (
            <button
              type="button"
              className="search-card-view-options"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (onViewOptions) {
                  onViewOptions(product);
                }
              }}
              onTouchEnd={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (onViewOptions) {
                  onViewOptions(product);
                }
              }}
              style={outOfStock ? { backgroundColor: '#ccc', color: '#999', cursor: 'not-allowed' } : { cursor: 'pointer' }}
              aria-label="View Options"
              disabled={outOfStock}
            >
              View Options
            </button>
          ) : (
            <button
              type="button"
              className="add-to-cart-btn"
              onClick={handleAddToCartClick}
              onTouchEnd={(e) => { e.stopPropagation(); }}
              style={outOfStock ? { backgroundColor: '#ccc', color: '#888', cursor: 'not-allowed', zIndex: 10, position: 'relative' } : { cursor: 'pointer', zIndex: 10, position: 'relative' }}
              aria-label="Add to cart"
              disabled={quantity === 0}
            >
              Add To Cart
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchCards;